/**
 * SEOHead
 *
 * Centralized SEO <head> manager using react-helmet-async. Pulls defaults from
 * `utils/analytics/seo` and supports dynamic overrides, structured data, breadcrumbs,
 * and performance preconnect/dns-prefetch hints.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getPageSEO, getOptimizedImageUrl } from '../../utils/analytics/seo';

const SEOHead = ({ 
  pageType = 'home', 
  customData = {}, 
  dynamicTitle = null,
  dynamicDescription = null,
  dynamicImage = null,
  dynamicKeywords = null,
  structuredDataOverride = null,
  breadcrumbs = null,
  isNoIndex = false
}) => {
  const seoConfig = getPageSEO(pageType, {
    ...customData,
    ...(dynamicTitle && { title: dynamicTitle }),
    ...(dynamicDescription && { description: dynamicDescription }),
    ...(dynamicImage && { image: dynamicImage }),
    ...(dynamicKeywords && { keywords: dynamicKeywords })
  });

  const structuredData = structuredDataOverride || seoConfig.structuredData;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoConfig.title}</title>
      <meta name="title" content={seoConfig.title} />
      <meta name="description" content={seoConfig.description} />
      <meta name="keywords" content={seoConfig.keywords} />
      <meta name="author" content={seoConfig.author} />
      <meta name="language" content={seoConfig.language} />
      
      {/* Robots and Indexing */}
      <meta name="robots" content={isNoIndex ? "noindex, nofollow" : seoConfig.robots} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={seoConfig.canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={seoConfig.ogType} />
      <meta property="og:url" content={seoConfig.canonicalUrl} />
      <meta property="og:title" content={seoConfig.title} />
      <meta property="og:description" content={seoConfig.description} />
      <meta property="og:image" content={getOptimizedImageUrl(seoConfig.image, 1200)} />
      <meta property="og:image:alt" content={seoConfig.title} />
      <meta property="og:site_name" content={seoConfig.ogSiteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta property="twitter:card" content={seoConfig.twitterCard} />
      <meta property="twitter:url" content={seoConfig.canonicalUrl} />
      <meta property="twitter:title" content={seoConfig.title} />
      <meta property="twitter:description" content={seoConfig.description} />
      <meta property="twitter:image" content={getOptimizedImageUrl(seoConfig.image, 1200)} />
      <meta property="twitter:image:alt" content={seoConfig.title} />
      <meta property="twitter:site" content={seoConfig.twitterSite} />
      <meta property="twitter:creator" content={seoConfig.twitterSite} />
      
      {/* Additional Performance and Security Meta Tags */}
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      <meta name="theme-color" content="#9333ea" />
      <meta name="msapplication-TileColor" content="#9333ea" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-capable" content="yes" />
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="preconnect" href="https://analytics.google.com" />
      
      {/* DNS Prefetch for better performance */}
      <link rel="dns-prefetch" href="https://use.typekit.net" />
      
      {/* Preload critical images */}
      {seoConfig.preloadImages && seoConfig.preloadImages.map((imagePath, index) => (
        <link 
          key={index}
          rel="preload" 
          href={getOptimizedImageUrl(imagePath)} 
          as="image"
          crossOrigin="anonymous"
        />
      ))}
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Breadcrumb Structured Data */}
      {breadcrumbs && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((crumb, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": crumb.name,
              "item": crumb.url
            }))
          })}
        </script>
      )}
      
      {/* Alternative hreflang for international SEO (if needed in future) */}
      <link rel="alternate" hrefLang="en" href={seoConfig.canonicalUrl} />
      
      {/* RSS/Atom feeds (if blog is added) */}
      {/* <link rel="alternate" type="application/rss+xml" title="Advocacy Lab Blog" href="/feed.xml" /> */}
      
      {/* Critical CSS for above-the-fold content */}
      {seoConfig.criticalCSS && (
        <style type="text/css">
          {seoConfig.criticalCSS}
        </style>
      )}
    </Helmet>
  );
};

export default SEOHead; 